package com.essntl.features.message.ui.client_profile

import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.essntl.features.client.ui.addeditclient.AddEditClientState
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.uikit.components.card.EsExpandableCardsSection
import com.essntl.uikit.components.chat.contact.ContactAvatar
import com.essntl.uikit.components.chat.contact.ContactUiState
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDate
import core.dateformat.ui.formatDateRange

@Composable
fun ClientProfileScreen(
    state: AddEditClientState,
    onNavigation: (ClientProfileNavigation) -> Unit,
    contactUiState: ContactUiState,
) {
    val isLoading = remember(state) { state.isLoading }

    val isFailure = remember(state) { state.isFailure }

    val client = state.getClient.data

    Column(
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (isLoading || isFailure) {
            if (isLoading) {
                EsSpinner(
                    modifier = Modifier
                        .padding(2.5f.spacing)
                        .align(Alignment.CenterHorizontally),
                )
            } else {
                Text(
                    text = "Something goes wrong!",
                    color = MaterialTheme.esColorScheme.textSecondary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    modifier = Modifier.padding(start = 2.5f.spacing),
                )
            }
        } else {
            Column(
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.fillMaxWidth(),
            ) {
                ContactAvatar(
                    initials = client?.toContactUiState?.initials ?: contactUiState.initials,
                    isOnline = false,
                )

                Spacer(modifier = Modifier.height(1.spacing))

                Text(
                    text = client?.prettyName ?: contactUiState.name ?: "Unknown",
                    style = MaterialTheme.esTypography.h4SemiBold20.copy(
                        color = MaterialTheme.esColorScheme.textPrimary,
                    ),
                )

                client?.email?.let { email ->
                    Spacer(modifier = Modifier.height(1.spacing))

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        EsSmallIcon(
                            res = EsIcons.Mail,
                            tint = MaterialTheme.esColorScheme.iconSecondary,
                        )

                        Spacer(modifier = Modifier.width(.5.spacing))

                        Text(
                            text = email,
                            style = MaterialTheme.esTypography.p1Medium16.copy(
                                color = MaterialTheme.esColorScheme.textSecondary,
                            ),
                        )
                    }
                }

                Spacer(modifier = Modifier.height(1.spacing))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                ) {
                    EsSmallIcon(
                        res = EsIcons.Phone,
                        tint = MaterialTheme.esColorScheme.iconSecondary,
                    )

                    Spacer(modifier = Modifier.width(.5.spacing))

                    Text(
                        text = client?.telephone ?: contactUiState.phoneNumber ?: "-",
                        style = MaterialTheme.esTypography.p1Medium16.copy(
                            color = MaterialTheme.esColorScheme.textSecondary,
                        ),
                    )
                }
            }

            Spacer(modifier = Modifier.height(3.spacing))

            Text(
                text = "Person info",
                style = MaterialTheme.esTypography.h565018.copy(
                    color = MaterialTheme.esColorScheme.textPrimary,
                ),
                modifier = Modifier.padding(start = 2.5f.spacing),
            )

            Spacer(modifier = Modifier.height(4.spacing))

            Text(
                text = "Date of birth",
                style = MaterialTheme.esTypography.p1Medium16.copy(
                    color = MaterialTheme.esColorScheme.textPrimary,
                ),
                modifier = Modifier.padding(start = 2.5f.spacing),
            )

            Spacer(modifier = Modifier.height(.5.spacing))

            Text(
                text = client?.dateOfBirth?.formatDate(".") ?: "-",
                style = MaterialTheme.esTypography.p2Regular14,
                modifier = Modifier.padding(start = 2.5f.spacing),
            )

            Spacer(modifier = Modifier.height(4.spacing))

            Text(
                text = "Travel agent",
                style = MaterialTheme.esTypography.p1Medium16.copy(
                    color = MaterialTheme.esColorScheme.textPrimary,
                ),
                modifier = Modifier.padding(start = 2.5f.spacing),
            )

            Spacer(modifier = Modifier.height(.5.spacing))

            Text(
                text = client?.travelAgent?.prettyName ?: "-",
                style = MaterialTheme.esTypography.p2Regular14,
                modifier = Modifier.padding(start = 2.5f.spacing),
            )

            client?.let { c ->
                Spacer(modifier = Modifier.height(5.spacing))

                state.getItineraries.data?.let { itineraries ->
                    if (itineraries.isNotEmpty()) {
                        EsExpandableCardsSection<ItineraryModel>(
                            items = itineraries,
                            title = "Itineraries",
                            cardTitle = { it.proposal?.title ?: "" },
                            cardSubtitle = {
                                listOf(
                                    it.proposal?.fromDate,
                                    it.proposal?.toDate,
                                ).formatDateRange() + '\n' + rememberNumberOfDaysPrettyString(
                                    it.proposal?.fromDate,
                                    it.proposal?.toDate,
                                )
                            },
                            cardActionButtons = { itinerary ->
                                EsSmallOutlineIconButton(
                                    onClick = {
                                        onNavigation(
                                            ClientProfileNavigation.NavigateToItinerary(
                                                itineraryId = itinerary.id,
                                            ),
                                        )
                                    },
                                    iconRes = EsIcons.Eye,
                                )
                            },
                        )
                    }
                }
            }
        }
    }
}